<template>
  <q-card flat bordered class="q-mb-md boloes-foto">
    <q-card-section class="row items-center q-pb-sm">
      <div class="text-subtitle1 text-weight-medium">Conferir bolão por foto</div>
      <q-space />
      <q-btn
        v-if="captura && !captura.lancamento"
        flat
        dense
        color="negative"
        label="Descartar foto"
        :disable="ocupado || capturaSubstituida || operacaoParaRepetir === 'recuperar-substituicao'"
        @click="descartar"
      />
    </q-card-section>

    <q-card-section v-if="!captura" class="q-pt-none">
      <div
        class="drop-area q-pa-md text-center"
        :class="{ 'drop-area--ativa': arrastando }"
        @dragover.prevent="arrastando = true"
        @dragleave.prevent="arrastando = false"
        @drop.prevent="receberDrop"
      >
        <div class="q-mb-sm">Selecione ou arraste uma foto do comprovante.</div>
        <div class="row justify-center q-gutter-sm">
          <q-btn outline color="primary" label="Escolher foto" :disable="envioDeArquivoAtivo" @click="$refs.arquivo.click()" />
          <q-btn outline color="primary" label="Abrir câmera" :disable="envioDeArquivoAtivo" @click="$refs.camera.click()" />
        </div>
        <input
          ref="arquivo"
          class="hidden"
          type="file"
          accept="image/jpeg,image/png,image/webp"
          :disabled="envioDeArquivoAtivo"
          @change="receberArquivo"
        />
        <input
          ref="camera"
          class="hidden"
          type="file"
          accept="image/*"
          capture="environment"
          :disabled="envioDeArquivoAtivo"
          @change="receberArquivo"
        />
        <div class="text-caption text-grey-7 q-mt-sm">
          JPG, PNG ou WebP; até 10 MB. No celular, escolha a galeria ou use a câmera.
        </div>
        <div v-if="erroCaptura" class="text-negative q-mt-sm" role="alert">{{ erroCaptura }}</div>
      </div>
      <div v-if="ocupado" class="row items-center q-gutter-sm q-mt-sm" role="status">
        <q-spinner color="primary" />
        <span>{{ mensagemEstado }}</span>
      </div>
      <q-btn
        v-if="capturaInicialIncerta"
        flat
        color="primary"
        label="Verificar envio"
        :disable="ocupado"
        @click="reconciliarCapturaInicial()"
      />
      <q-btn
        v-else-if="erroCaptura && arquivoPendente"
        flat
        color="primary"
        label="Tentar novamente"
        :disable="ocupado"
        @click="enviarArquivo(arquivoPendente)"
      />
    </q-card-section>

    <q-card-section v-if="captura" class="q-pt-none">
      <q-banner v-if="conflito && capturaEncerrada" rounded class="bg-orange-1 text-orange-10 q-mb-md">
        A captura foi encerrada no servidor. Esta revisão está somente para leitura e as correções locais não podem ser salvas.
        <div v-if="resumoCorrecoesRecuperaveis" class="q-mt-sm">
          <span v-if="capturaSubstituidaComDestino">A captura antiga foi substituída e não pode ser descartada. Tente carregar a nova captura novamente; suas correções serão reaplicadas:</span>
          <span v-else-if="capturaSubstituida">O servidor marcou esta captura como substituída, mas não informou um replacement_id válido. Suas correções estão preservadas; a captura antiga não pode ser descartada.</span>
          <span v-else>Copie estas correções antes de descartar a foto para poder reaproveitá-las:</span>
          <textarea
            ref="resumoCorrecoes"
            class="resumo-correcoes q-mt-xs"
            :value="resumoCorrecoesRecuperaveis"
            aria-label="Resumo selecionável das correções locais"
            rows="6"
            readonly
          />
          <q-btn
            flat
            dense
            color="primary"
            label="Copiar correções"
            @click="copiarCorrecoes"
          />
          <div v-if="mensagemCopiaCorrecoes" role="status" class="q-mt-xs">
            {{ mensagemCopiaCorrecoes }}
          </div>
        </div>
        <div v-else class="q-mt-sm">
          <span v-if="capturaSubstituidaComDestino">A captura antiga foi substituída e não pode ser descartada. Tente carregar a nova captura novamente.</span>
          <span v-else-if="capturaSubstituida">O servidor marcou esta captura como substituída, mas não informou um replacement_id válido. A captura antiga não pode ser descartada.</span>
          <span v-else>Não há correções locais pendentes para recuperar. Você pode descartar esta foto.</span>
        </div>
      </q-banner>
      <q-banner v-else-if="conflito" rounded class="bg-orange-1 text-orange-10 q-mb-md">
        Uma versão atual da revisão foi carregada. Suas correções locais foram mantidas; confira os
        campos e salve novamente para aplicar sobre a versão atual.
        <div v-if="rotulosConflitos.length" class="q-mt-xs">
          Campos alterados também no servidor: {{ rotulosConflitos.join(', ') }}. Revise estes valores antes de salvar.
        </div>
      </q-banner>
      <q-banner v-if="correcoesMantidas" rounded class="bg-blue-1 text-blue-10 q-mb-md">
        Mantivemos suas correções enquanto a foto era processada novamente.
      </q-banner>
      <div v-if="ocupado" class="row items-center q-gutter-sm q-mb-md" role="status">
        <q-spinner color="primary" />
        <span>{{ mensagemEstado }}</span>
      </div>
      <div v-if="erroOperacao" class="text-negative q-mb-md" role="alert">
        {{ erroOperacao }}
        <q-btn
          v-if="!capturaEncerrada || ['descartar', 'recuperar-substituicao'].includes(operacaoParaRepetir)"
          flat
          dense
          color="primary"
          label="Tentar novamente"
          :disable="ocupado"
          @click="repetirOperacao"
        />
      </div>
      <div v-if="erroCaptura" class="text-negative q-mb-md" role="alert">
        {{ erroCaptura }}
        <q-btn v-if="!capturaEncerrada" flat dense color="primary" label="Cancelar" @click="erroCaptura = null" />
      </div>

      <div class="row q-col-gutter-md">
        <div class="col-12 col-md-5">
          <div class="preview-foto flex flex-center">
            <img v-if="previewUrl" :src="previewUrl" alt="Foto privada do comprovante" />
            <span v-else class="text-grey-7">Prévia privada indisponível</span>
          </div>
          <div v-if="erroPreview" class="text-negative q-mt-sm" role="alert">
            {{ erroPreview }}
            <q-btn flat dense color="primary" label="Tentar carregar prévia" @click="recarregarPreview" />
          </div>
          <div class="row q-gutter-sm q-mt-sm">
            <q-btn
              outline
              color="primary"
              label="Substituir foto"
              :disable="envioDeArquivoAtivo || capturaEncerrada"
              @click="$refs.substituir.click()"
            />
            <input
              ref="substituir"
              class="hidden"
              type="file"
              accept="image/jpeg,image/png,image/webp"
              :disabled="envioDeArquivoAtivo || capturaEncerrada"
              @change="receberArquivo"
            />
          </div>
        </div>
        <div class="col-12 col-md-7">
          <div class="row q-col-gutter-sm">
            <div
              v-for="campo in camposTexto"
              :key="campo.chave"
              class="col-12 col-sm-6"
              :class="{ 'campo-sorteio': campo.chave === 'data_sorteio' }"
            >
              <q-input
                v-model="documento[campo.chave]"
                :label="campo.rotulo"
                :title="campo.descricao"
                :disable="ocupado || salvando || capturaEncerrada || operacaoParaRepetir === 'recuperar-substituicao'"
                clearable
                :hint="documento[campo.chave] === null ? 'Não informado' : ''"
                @update:model-value="marcarEditado"
              />
            </div>
            <div v-for="campo in camposValor" :key="campo.chave" class="col-12 col-sm-6">
              <q-input
                v-model="documento[campo.chave]"
                :label="campo.rotulo"
                :title="campo.descricao"
                prefix="R$"
                placeholder="0,00"
                :disable="ocupado || salvando || capturaEncerrada || operacaoParaRepetir === 'recuperar-substituicao'"
                clearable
                inputmode="decimal"
                :hint="documento[campo.chave] === null ? 'Não informado' : ''"
                @update:model-value="marcarEditado"
              />
            </div>
          </div>
          <div v-if="dataSorteioFormatada" class="aviso-sorteio q-mt-sm q-pa-sm" role="note">
            Sorteio em <b>{{ dataSorteioFormatada }}</b>. Confira na foto: a leitura às vezes troca pela data de emissão.
          </div>
          <q-banner v-if="divergenciaTotal" dense rounded class="bg-orange-1 text-orange-10 q-mt-sm" role="alert">
            {{ divergenciaTotal }}
          </q-banner>
          <q-banner v-if="recibosNaFoto > 1" dense rounded class="bg-orange-1 text-orange-10 q-mt-sm" role="alert">
            A foto mostra {{ recibosNaFoto }} recibos e só um foi lido. Envie uma foto por recibo.
          </q-banner>
          <div class="row items-center q-gutter-sm q-mt-md">
            <q-btn
              color="primary"
              label="Salvar revisão"
              :loading="salvando"
              :disable="ocupado || capturaEncerrada || !alterado || ['atualizar-conflito', 'recuperar-substituicao'].includes(operacaoParaRepetir)"
              @click="salvarRevisao"
            />
            <q-btn
              v-if="estadoExtracao === 'erro' && !capturaEncerrada && !['recuperar-substituicao', 'atualizar-conflito'].includes(operacaoParaRepetir)"
              flat
              color="primary"
              label="Tentar extração novamente"
              :disable="ocupado"
              @click="extrair()"
            />
          </div>
        </div>
      </div>
      <lancar-compra-boloes-foto
        v-if="prontaParaLancar"
        :captura="captura"
        :compra-aberta="compraAberta"
        :reaproveitar-compra-do-caixa="reaproveitarCompraDoCaixa"
        :bloqueado="alterado || ocupado || salvando"
        @lancada="aoLancar"
        @proxima="limpar"
      />
    </q-card-section>
  </q-card>
</template>

<script>
import api from '@/services/boloes-foto';
import LancarCompraBoloesFoto from '@/components/Caixas/boloes-foto/lancar-compra.vue';
import { centavosParaInput, inputParaCentavos } from '@/components/Caixas/boloes-foto/dinheiro';

const camposTexto = [
  { chave: 'modalidade', rotulo: 'Modalidade', descricao: 'Modalidade' },
  { chave: 'concurso', rotulo: 'Concurso', descricao: 'Concurso' },
  { chave: 'identificacao_cota', rotulo: 'Cota', descricao: 'Número da cota (x em COTA x/y)' },
  { chave: 'total_cotas', rotulo: 'Total de cotas', descricao: 'Total de cotas do bolão (y em COTA x/y)' },
  { chave: 'perfil', rotulo: 'Perfil', descricao: 'Perfil impresso' },
  { chave: 'data_sorteio', rotulo: 'Sorteio', descricao: 'Data do sorteio, na linha do concurso' },
];
const camposValor = [
  { chave: 'valor_cota_centavos', rotulo: 'Valor da cota', descricao: 'Valor impresso por cota' },
  { chave: 'tarifa_centavos', rotulo: 'Tarifa', descricao: 'Tarifa de serviço impressa' },
  { chave: 'total_documental_centavos', rotulo: 'Total', descricao: 'Total impresso no recibo' },
];
const campos = [...camposTexto, ...camposValor].map(campo => campo.chave);
const MAX_SUBSTITUICOES_ENCADEADAS = 12;

function idCapturaValido(id) {
  return (typeof id === 'string' && id.trim().length > 0)
    || (typeof id === 'number' && Number.isSafeInteger(id) && id > 0);
}
const documentoVazio = () => campos.reduce((doc, chave) => ({ ...doc, [chave]: null }), {});
const mensagemErro = error => (error && error.message) || 'Não foi possível processar a foto.';
const substituicaoRejeitadaDefinitivamente = error => {
  const status = Number(error && (error.status || (error.response && error.response.status)));
  if (status === 422 || status === 410) return true;
  if (status !== 409) return false;
  const body = error && error.response && error.response.data || {};
  const dados = body.data || {};
  const captura = body.capture || dados.capture || {};
  const caixa = captura.movcaixa || captura.cashbox || captura.caixa || {};
  const detalhes = [
    body.code, body.error, body.reason, body.status, body.state, body.message,
    dados.code, dados.error, dados.reason, dados.status, dados.state, dados.message,
    captura.status, captura.state, caixa.status, caixa.state, error && error.message,
  ]
    .filter(Boolean)
    .join(' ')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();
  return /caixa.{0,40}(?:fech|encerr)|(?:fech|encerr).{0,40}caixa|caixa.{0,40}nao.{0,20}abert|cash.?box.{0,40}closed|closed.{0,40}cash.?box/.test(detalhes);
};
const estadoCapturaEncerrado = captura => {
  const caixa = captura && (captura.movcaixa || captura.cashbox || captura.caixa);
  const extracao = captura && (captura.extraction || captura.extracao);
  const estados = [captura && captura.status, captura && captura.state, extracao && extracao.status, caixa && caixa.status, caixa && caixa.state]
    .filter(Boolean)
    .map(estado => String(estado).normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase());
  return estados.some(estado => ['expired', 'expirado', 'expirada', 'closed', 'fechado', 'encerrado', 'cancelled', 'canceled', 'descartado', 'replaced'].includes(estado));
};
const capturaFoiSubstituida = captura => String(captura && (captura.status || captura.state) || '').toLowerCase() === 'replaced';
const primeiroEnvioAmbiguo = error => {
  const status = Number(error && (error.status || (error.response && error.response.status)));
  return !status || status === 408 || status === 409 || status >= 500;
};
const identificador = value => (value === null || value === undefined || value === '' ? null : String(value));
const novaChaveIdempotencia = () => {
  const cryptoApi = typeof window !== 'undefined' && window.crypto;
  if (cryptoApi && typeof cryptoApi.randomUUID === 'function') return cryptoApi.randomUUID();

  const bytes = new Uint8Array(16);
  if (cryptoApi && typeof cryptoApi.getRandomValues === 'function') {
    cryptoApi.getRandomValues(bytes);
  } else {
    for (let i = 0; i < bytes.length; i += 1) bytes[i] = Math.floor(Math.random() * 256);
  }
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, byte => (`0${byte.toString(16)}`).slice(-2)).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
};

const copiarSelecao = () => {
  try {
    return Boolean(document.execCommand && document.execCommand('copy'));
  } catch (error) {
    return false;
  }
};

export default {
  name: 'revisao-captura-boloes-foto',
  components: { LancarCompraBoloesFoto },
  emits: ['lancada'],
  props: {
    movcaixaId: {
      type: [Number, String],
      default: null,
    },
    compraAberta: {
      type: Object,
      default: null,
    },
    reaproveitarCompraDoCaixa: {
      type: Boolean,
      default: false,
    },
    arquivoInicial: {
      type: File,
      default: null,
    },
  },
  data() {
    return {
      captura: null,
      previewPrivado: null,
      documento: documentoVazio(),
      estadoExtracao: 'ociosa',
      ocupado: false,
      salvando: false,
      arrastando: false,
      erroCaptura: null,
      erroOperacao: null,
      erroPreview: null,
      conflito: false,
      capturaInicialIncerta: false,
      chaveIdempotenciaCaptura: null,
      contextoCapturaInicial: null,
      capturaEncerrada: false,
      conflitosCampos: [],
      correcoesMantidas: false,
      mensagemCopiaCorrecoes: null,
      arquivoPendente: null,
      capturaParaRecuperar: null,
      replacementIdParaRecuperar: null,
      geracao: 0,
      previewRequisicao: 0,
      operacaoPendente: null,
      operacaoParaRepetir: null,
      baseDocumento: JSON.stringify(documentoVazio()),
    };
  },
  computed: {
    camposTexto: () => camposTexto,
    camposValor: () => camposValor,
    capturaSubstituida() {
      return capturaFoiSubstituida(this.captura);
    },
    capturaSubstituidaComDestino() {
      return this.capturaSubstituida && idCapturaValido(this.captura.replacement_id);
    },
    previewUrl() {
      return this.previewPrivado;
    },
    envioDeArquivoAtivo() {
      return this.capturaInicialIncerta
        || this.operacaoParaRepetir === 'recuperar-substituicao'
        || (this.ocupado && ['enviando', 'substituindo'].includes(this.estadoExtracao));
    },
    alterado() {
      return JSON.stringify(this.documento) !== this.baseDocumento;
    },
    prontaParaLancar() {
      if (!this.captura || this.capturaSubstituida || this.revisaoAtual() < 1) return false;
      return Boolean(this.captura.lancamento) || !this.capturaEncerrada;
    },
    recibosNaFoto() {
      const extracao = this.captura && this.captura.extraction;
      return extracao ? Number(extracao.recibos_na_foto) || 0 : 0;
    },
    divergenciaTotal() {
      const valor = inputParaCentavos(this.documento.valor_cota_centavos);
      const tarifa = inputParaCentavos(this.documento.tarifa_centavos);
      const total = inputParaCentavos(this.documento.total_documental_centavos);
      if (![valor, tarifa, total].every(Number.isInteger) || valor + tarifa === total) return null;
      return `Valor da cota (R$ ${centavosParaInput(valor)}) + tarifa (R$ ${centavosParaInput(tarifa)}) `
        + `dá R$ ${centavosParaInput(valor + tarifa)}, mas o total impresso é R$ ${centavosParaInput(total)}. `
        + 'Confira os valores na foto.';
    },
    dataSorteioFormatada() {
      const valor = this.documento.data_sorteio;
      if (!valor) return null;
      const iso = String(valor).match(/^(\d{4})-(\d{2})-(\d{2})$/);
      if (!iso) return String(valor);
      const dia = new Date(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3]));
      return `${iso[3]}/${iso[2]}/${iso[1]}, ${dia.toLocaleDateString('pt-BR', { weekday: 'long' })}`;
    },
    mensagemEstado() {
      if (this.estadoExtracao === 'extraindo') return 'Lendo os dados da foto…';
      if (this.estadoExtracao === 'reconciliando') return 'Confirmando o envio da foto…';
      if (this.estadoExtracao === 'enviando') return 'Enviando foto…';
      if (this.estadoExtracao === 'substituindo') return 'Substituindo foto…';
      if (this.salvando) return 'Salvando revisão…';
      return 'Carregando captura…';
    },
    rotulosConflitos() {
      const listaCampos = [...camposTexto, ...camposValor];
      return this.conflitosCampos.map(chave => {
        const campo = listaCampos.find(item => item.chave === chave);
        return campo ? campo.rotulo : chave;
      });
    },
    resumoCorrecoesRecuperaveis() {
      const documentoBase = JSON.parse(this.baseDocumento);
      const rotulos = [...camposTexto, ...camposValor].reduce((lista, campo) => {
        lista[campo.chave] = campo.rotulo;
        return lista;
      }, {});
      const correcoes = campos.reduce((resultado, chave) => {
        if (this.documento[chave] !== documentoBase[chave]) {
          resultado[rotulos[chave]] = this.documento[chave];
        }
        return resultado;
      }, {});
      return Object.keys(correcoes).length ? JSON.stringify(correcoes, null, 2) : '';
    },
    sessaoCapturaId() {
      const getters = this.$store && this.$store.getters || {};
      const empresa = getters.dataEmpresa || {};
      const usuario = getters.dataUser || {};
      return `${identificador(empresa.id) || ''}:${identificador(usuario.id) || ''}`;
    },
  },
  watch: {
    arquivoInicial(novo) {
      if (novo) this.enviarArquivo(novo);
    },
    movcaixaId(novo, anterior) {
      if (String(novo) !== String(anterior)) this.limpar();
    },
    sessaoCapturaId(novo, anterior) {
      if (novo !== anterior) this.limpar();
    },
  },
  mounted() {
    if (this.arquivoInicial) this.enviarArquivo(this.arquivoInicial);
  },
  beforeUnmount() {
    this.geracao += 1;
    this.revogarPreview(this.previewPrivado);
    this.previewPrivado = null;
  },
  methods: {
    receberDrop(event) {
      this.arrastando = false;
      if (this.envioDeArquivoAtivo) return;
      const file = event.dataTransfer && event.dataTransfer.files[0];
      if (file) this.enviarArquivo(file);
    },
    receberArquivo(event) {
      const file = event.target.files && event.target.files[0];
      event.target.value = '';
      if (this.envioDeArquivoAtivo) return;
      if (file) this.enviarArquivo(file);
    },
    validarArquivo(file) {
      const formatos = ['image/jpeg', 'image/png', 'image/webp'];
      if (!formatos.includes(file.type)) return 'Formato não suportado. Escolha uma imagem JPG, PNG ou WebP.';
      if (file.size > 10 * 1024 * 1024) return 'A imagem excede o limite de 10 MB.';
      if (!file.size) return 'O arquivo selecionado está vazio.';
      return null;
    },
    normalizarDocumento(documento) {
      const origem = documento || {};
      const resultado = campos.reduce((result, chave) => {
        const valor = origem[chave];
        result[chave] = valor === undefined || valor === '' ? null : valor;
        return result;
      }, {});
      camposValor.forEach(campo => {
        resultado[campo.chave] = centavosParaInput(resultado[campo.chave]);
      });
      return resultado;
    },
    adotarCaptura(atual) {
      this.captura = atual;
      this.capturaEncerrada = estadoCapturaEncerrado(atual) || Boolean(atual && atual.lancamento);
    },
    aoLancar(resposta) {
      const dados = resposta || {};
      if (dados.capture) {
        this.adotarCaptura(dados.capture);
      } else if (dados.lancamento) {
        this.adotarCaptura({ ...this.captura, lancamento: dados.lancamento });
      }
      if (dados.lancamento) this.$emit('lancada', dados.lancamento);
    },
    atualizarCaptura(resposta) {
      const dados = resposta || {};
      const atual = dados.capture || dados.captura || dados;
      const extracao = atual.extraction || {};
      this.adotarCaptura(atual);
      const documento = this.normalizarDocumento(
        dados.document || extracao.document || atual.documento || atual.revisao
      );
      this.documento = documento;
      this.baseDocumento = JSON.stringify(documento);
      this.conflitosCampos = [];
      this.estadoExtracao = ['failed', 'incomplete', 'timeout', 'unavailable', 'refused', 'erro', 'falha']
        .includes(extracao.status)
        ? 'erro'
        : (extracao.status === 'pending' || !extracao.status ? 'ociosa' : 'concluida');
    },
    revisaoAtual() {
      const extracao = this.captura && this.captura.extraction;
      return extracao && Number.isInteger(Number(extracao.current_revision))
        ? Number(extracao.current_revision)
        : 0;
    },
    carregarCapturaDoErro(error) {
      const body = error && error.response && error.response.data;
      const capture = body && body.data && body.data.capture;
      if (capture) this.atualizarCaptura({ capture });
      return capture || null;
    },
    async carregarPreview(id, geracao) {
      const requisicao = ++this.previewRequisicao;
      this.revogarPreview(this.previewPrivado);
      this.previewPrivado = null;
      this.erroPreview = null;
      try {
        const preview = await api.preview(id);
        if (geracao !== this.geracao
          || requisicao !== this.previewRequisicao
          || !this.captura
          || String(this.captura.id) !== String(id)) return;
        this.previewPrivado = typeof preview === 'string' ? preview : URL.createObjectURL(preview);
        return true;
      } catch (error) {
        if (geracao === this.geracao
          && requisicao === this.previewRequisicao
          && this.captura
          && String(this.captura.id) === String(id)) {
          this.erroPreview = `Não foi possível carregar a prévia privada: ${mensagemErro(error)}`;
        }
        return false;
      }
    },
    recarregarPreview() {
      if (!this.captura || !this.captura.id) return;
      return this.carregarPreview(this.captura.id, this.geracao);
    },
    revogarPreview(url) {
      if (url && url.indexOf('blob:') === 0 && typeof URL.revokeObjectURL === 'function') {
        URL.revokeObjectURL(url);
      }
    },
    obterContextoCapturaInicial() {
      const getters = this.$store && this.$store.getters || {};
      const empresa = getters.dataEmpresa || {};
      const usuario = getters.dataUser || {};
      return {
        movcaixaId: identificador(this.movcaixaId),
        empresaId: identificador(empresa.id),
        operadorId: identificador(usuario.id),
      };
    },
    contextoCapturaInicialAindaAtual() {
      const original = this.contextoCapturaInicial;
      if (!original) return false;
      const atual = this.obterContextoCapturaInicial();
      return original.movcaixaId === atual.movcaixaId
        && original.empresaId === atual.empresaId
        && original.operadorId === atual.operadorId;
    },
    podeReenviarCapturaInicial() {
      const contexto = this.contextoCapturaInicial;
      return this.contextoCapturaInicialAindaAtual()
        && contexto
        && contexto.empresaId !== null
        && contexto.operadorId !== null
        && Boolean(this.arquivoPendente);
    },
    capturaPertenceAoCaixaInicial(capture) {
      return Boolean(
        capture
        && capture.id
        && this.contextoCapturaInicial
        && identificador(capture.movcaixa_id) === this.contextoCapturaInicial.movcaixaId
      );
    },
    marcarCapturaInicialIncerta(mensagem) {
      this.capturaInicialIncerta = true;
      this.operacaoParaRepetir = 'reconciliar-captura-inicial';
      this.estadoExtracao = 'erro';
      this.erroCaptura = mensagem;
    },
    async adotarCapturaInicial(resposta, geracao) {
      if (geracao !== this.geracao) return false;
      if (!this.contextoCapturaInicialAindaAtual()) {
        this.limpar();
        return false;
      }
      const dados = resposta || {};
      const capture = dados.capture || dados.captura || dados;
      if (!this.capturaPertenceAoCaixaInicial(capture)) {
        this.marcarCapturaInicialIncerta(
          'A tentativa foi encontrada, mas pertence a outro caixa. O envio permanece bloqueado para evitar uma captura duplicada.'
        );
        return false;
      }

      this.atualizarCaptura({ capture });
      this.capturaInicialIncerta = false;
      this.operacaoParaRepetir = null;
      this.erroCaptura = null;
      this.erroOperacao = null;
      this.arquivoPendente = null;
      try {
        await this.carregarPreview(capture.id, geracao);
        await this.extrair(geracao);
      } finally {
        if (geracao === this.geracao) {
          this.chaveIdempotenciaCaptura = null;
          this.contextoCapturaInicial = null;
        }
      }
      return true;
    },
    adotarCapturaInicialTerminal(capture) {
      if (!this.capturaPertenceAoCaixaInicial(capture)) {
        this.marcarCapturaInicialIncerta(
          'A tentativa encerrada foi encontrada em outro caixa. O envio permanece bloqueado para evitar uma captura duplicada.'
        );
        return;
      }
      this.atualizarCaptura({ capture });
      this.capturaEncerrada = true;
      this.capturaInicialIncerta = false;
      this.chaveIdempotenciaCaptura = null;
      this.contextoCapturaInicial = null;
      this.operacaoParaRepetir = null;
      this.arquivoPendente = null;
      this.estadoExtracao = 'erro';
      this.erroCaptura = 'A tentativa de envio foi encerrada no servidor. Não reenvie esta foto.';
    },
    async reconciliarCapturaInicial(geracao = this.geracao) {
      if (!this.capturaInicialIncerta || !this.chaveIdempotenciaCaptura || this.ocupado) return;
      if (!this.contextoCapturaInicialAindaAtual()) {
        this.limpar();
        return;
      }

      const chave = this.chaveIdempotenciaCaptura;
      const contexto = { ...this.contextoCapturaInicial };
      this.ocupado = true;
      this.estadoExtracao = 'reconciliando';
      this.erroCaptura = null;
      try {
        let resposta;
        try {
          resposta = await api.consultarTentativa(chave);
        } catch (error) {
          if (Number(error && (error.status || (error.response && error.response.status))) !== 404) {
            throw error;
          }
          if (geracao !== this.geracao) return;
          if (!this.contextoCapturaInicialAindaAtual()) {
            this.limpar();
            return;
          }
          if (!this.podeReenviarCapturaInicial()) {
            this.marcarCapturaInicialIncerta(
              'Não foi possível localizar a tentativa para a sessão atual. Confirme o operador e o caixa antes de continuar.'
            );
            return;
          }

          try {
            resposta = await api.criar(this.arquivoPendente, contexto.movcaixaId, chave);
          } catch (erroReenvio) {
            if (geracao !== this.geracao) return;
            if (!this.contextoCapturaInicialAindaAtual()) {
              this.limpar();
              return;
            }
            if (primeiroEnvioAmbiguo(erroReenvio)) {
              this.marcarCapturaInicialIncerta(
                `${mensagemErro(erroReenvio)} A tentativa continua incerta. Verifique o envio novamente antes de selecionar outra foto.`
              );
            } else {
              this.capturaInicialIncerta = false;
              this.operacaoParaRepetir = 'enviar';
              this.estadoExtracao = 'erro';
              this.erroCaptura = mensagemErro(erroReenvio);
            }
            return;
          }
          if (geracao !== this.geracao) return;
          if (!this.contextoCapturaInicialAindaAtual()) {
            this.limpar();
            return;
          }
          await this.adotarCapturaInicial(resposta, geracao);
          return;
        }

        if (geracao !== this.geracao) return;
        if (!this.contextoCapturaInicialAindaAtual()) {
          this.limpar();
          return;
        }
        const tentativa = resposta && (resposta.attempt || resposta.tentativa) || resposta || {};
        const capture = tentativa.capture || tentativa.captura;
        if (tentativa.status === 'completed') {
          await this.adotarCapturaInicial({ capture }, geracao);
        } else if (tentativa.status === 'terminal') {
          this.adotarCapturaInicialTerminal(capture);
        } else if (tentativa.status === 'pending') {
          this.marcarCapturaInicialIncerta(
            'O servidor ainda está processando o envio. Verifique novamente em instantes.'
          );
        } else {
          this.marcarCapturaInicialIncerta(
            'O servidor retornou um estado desconhecido para esta tentativa. Verifique novamente antes de continuar.'
          );
        }
      } catch (error) {
        if (geracao === this.geracao) {
          if (!this.contextoCapturaInicialAindaAtual()) {
            this.limpar();
          } else {
            this.marcarCapturaInicialIncerta(
              `Não foi possível confirmar o envio da foto: ${mensagemErro(error)} Verifique novamente antes de selecionar outra foto.`
            );
          }
        }
      } finally {
        if (geracao === this.geracao) this.ocupado = false;
      }
    },
    async enviarArquivo(file) {
      if (this.capturaInicialIncerta || this.capturaEncerrada || this.operacaoParaRepetir === 'recuperar-substituicao' || this.envioDeArquivoAtivo) return;
      const erro = this.validarArquivo(file);
      if (erro) {
        this.erroCaptura = erro;
        this.arquivoPendente = null;
        return;
      }
      const substitui = Boolean(this.captura && this.captura.id);
      const estadoAnterior = this.estadoExtracao;
      const geracao = ++this.geracao;
      if (substitui) {
        this.salvando = false;
        this.operacaoPendente = null;
      }
      if (!substitui && !this.movcaixaId) {
        this.erroCaptura = 'Não foi possível identificar o caixa aberto para anexar a foto.';
        this.arquivoPendente = null;
        return;
      }
      if (!substitui && (!this.chaveIdempotenciaCaptura || this.arquivoPendente !== file)) {
        this.chaveIdempotenciaCaptura = novaChaveIdempotencia();
        this.contextoCapturaInicial = this.obterContextoCapturaInicial();
      }
      this.arquivoPendente = file;
      this.erroCaptura = null;
      this.erroOperacao = null;
      this.erroPreview = null;
      this.conflito = false;
      this.correcoesMantidas = false;
      this.operacaoParaRepetir = null;
      this.estadoExtracao = substitui ? 'substituindo' : 'enviando';
      this.ocupado = true;
      try {
        const resposta = substitui
          ? await api.substituir(this.captura.id, file)
          : await api.criar(file, this.movcaixaId, this.chaveIdempotenciaCaptura);
        if (geracao !== this.geracao) return;
        if (!substitui && !this.contextoCapturaInicialAindaAtual()) {
          this.limpar();
          return;
        }
        if (!substitui) {
          await this.adotarCapturaInicial(resposta, geracao);
          return;
        }
        this.atualizarCaptura(resposta);
        await this.carregarPreview(this.captura.id, geracao);
        await this.extrair(geracao);
        this.arquivoPendente = null;
      } catch (error) {
        if (geracao === this.geracao) {
          if (!substitui && !this.contextoCapturaInicialAindaAtual()) {
            this.limpar();
            return;
          }
          if (substitui && substituicaoRejeitadaDefinitivamente(error)) {
            this.capturaParaRecuperar = null;
            this.operacaoParaRepetir = null;
            this.estadoExtracao = estadoAnterior;
            const status = Number(error.status || (error.response && error.response.status));
            if (status === 422) {
              this.erroCaptura = `${mensagemErro(error)} A substituição foi rejeitada pelo servidor; escolha uma foto para tentar novamente.`;
            } else {
              this.capturaEncerrada = true;
              this.erroCaptura = `${mensagemErro(error)} A captura está encerrada ou expirada. Descarte-a para liberar o envio de outra foto.`;
            }
          } else if (substitui) {
            this.capturaParaRecuperar = this.captura.id;
            this.operacaoParaRepetir = 'recuperar-substituicao';
            const recuperada = await this.recuperarSubstituicao(this.captura.id, geracao, mensagemErro(error));
            if (!recuperada && geracao === this.geracao) this.estadoExtracao = 'erro';
          } else {
            if (primeiroEnvioAmbiguo(error)) {
              this.marcarCapturaInicialIncerta(
                `${mensagemErro(error)} O envio pode ter sido recebido. Estou confirmando o resultado para evitar uma captura duplicada.`
              );
              this.ocupado = false;
              await this.reconciliarCapturaInicial(geracao);
            } else {
              this.erroCaptura = mensagemErro(error);
              this.operacaoParaRepetir = 'enviar';
              this.estadoExtracao = 'erro';
            }
          }
        }
      } finally {
        if (geracao === this.geracao) this.ocupado = false;
      }
    },
    async recuperarSubstituicao(captureId, geracao = this.geracao, erroOriginal = '') {
      const geracaoAtual = geracao;
      const documentoLocal = { ...this.documento };
      const documentoBaseLocal = JSON.parse(this.baseDocumento);
      let replacementId = null;
      this.capturaParaRecuperar = captureId;
      this.ocupado = true;
      this.estadoExtracao = 'substituindo';
      try {
        const respostaAntiga = await api.consultar(captureId);
        if (geracaoAtual !== this.geracao) return;
        const dadosAntigos = respostaAntiga || {};
        const capturaAntiga = dadosAntigos.capture || dadosAntigos.captura || dadosAntigos;
        this.adotarCaptura(capturaAntiga);
        if (capturaFoiSubstituida(capturaAntiga)) {
          const recuperada = await this.seguirCadeiaSubstituicoes(
            capturaAntiga,
            documentoBaseLocal,
            documentoLocal,
            geracaoAtual,
            []
          );
          if (recuperada && geracaoAtual === this.geracao) this.arquivoPendente = null;
          return recuperada;
        }
        if (estadoCapturaEncerrado(capturaAntiga)) {
          this.capturaEncerrada = true;
          this.mesclarConflito(
            documentoBaseLocal,
            documentoLocal,
            this.normalizarDocumento(capturaAntiga.extraction && capturaAntiga.extraction.document)
          );
          this.capturaParaRecuperar = null;
          this.operacaoParaRepetir = null;
          this.erroOperacao = null;
          this.erroCaptura = `${erroOriginal || 'A captura foi encerrada.'} A captura está encerrada ou expirada. Descarte-a para liberar o envio de outra foto.`;
          this.estadoExtracao = 'erro';
          return true;
        }
        if (!capturaAntiga.replacement_id) {
          this.erroOperacao = `${erroOriginal || 'Não foi possível substituir a foto.'} O resultado da substituição ainda é incerto. Consulte novamente antes de tentar outra substituição.`;
          this.operacaoParaRepetir = 'recuperar-substituicao';
          this.estadoExtracao = 'erro';
          return false;
        }

        replacementId = capturaAntiga.replacement_id;
        this.replacementIdParaRecuperar = replacementId;
        this.operacaoParaRepetir = 'recuperar-substituicao';
        this.erroOperacao = `${erroOriginal || 'Não foi possível substituir a foto.'} O resultado da substituição ainda é incerto. Consulte novamente antes de tentar outra substituição.`;
        this.estadoExtracao = 'erro';
        return false;
      } catch (error) {
        if (geracaoAtual === this.geracao) {
          if (replacementId) {
            this.replacementIdParaRecuperar = replacementId;
            this.capturaEncerrada = true;
            this.conflito = true;
            this.mesclarConflito(
              documentoBaseLocal,
              documentoLocal,
              this.normalizarDocumento(this.captura && this.captura.extraction && this.captura.extraction.document)
            );
          }
          this.erroOperacao = `Não foi possível confirmar a substituição da foto. ${mensagemErro(error)}`;
          this.operacaoParaRepetir = 'recuperar-substituicao';
          this.estadoExtracao = 'erro';
        }
        return false;
      } finally {
        if (geracaoAtual === this.geracao) this.ocupado = false;
      }
    },
    async extrair(geracaoAtual = this.geracao) {
      if (geracaoAtual !== this.geracao || !this.captura || !this.captura.id || this.capturaEncerrada) return;
      const geracao = geracaoAtual;
      const captureId = this.captura.id;
      const preservarDocumento = this.alterado;
      const documentoLocal = { ...this.documento };
      const documentoBaseLocal = JSON.parse(this.baseDocumento);
      const conflitosPreservados = [...this.conflitosCampos];
      const correcoesLocais = preservarDocumento
        ? Object.keys(documentoLocal).reduce((correcoes, campo) => {
          if (documentoLocal[campo] !== documentoBaseLocal[campo]) {
            correcoes[campo] = documentoLocal[campo];
          }
          return correcoes;
        }, {})
        : {};
      this.estadoExtracao = 'extraindo';
      this.ocupado = true;
      this.erroOperacao = null;
      this.operacaoPendente = 'extrair';
      try {
        const resposta = await api.extrair(this.captura.id, this.revisaoAtual());
        if (geracao !== this.geracao || !this.captura || this.captura.id !== captureId) return;
        this.atualizarCaptura(resposta);
        if (preservarDocumento && this.captura.id === captureId) {
          this.restaurarCorrecoes(correcoesLocais, conflitosPreservados);
        }
        this.operacaoParaRepetir = null;
      } catch (error) {
        if (geracao === this.geracao) {
          const status = Number(error.status || (error.response && error.response.status));
          this.estadoExtracao = 'erro';
          if (status === 409) {
            this.conflito = true;
            try {
              const respostaAtual = await api.consultar(captureId);
              if (geracao !== this.geracao || !this.captura || this.captura.id !== captureId) return;
              const dados = respostaAtual || {};
              const atual = dados.capture || dados.captura || dados;
              this.adotarCaptura(atual);
              if (capturaFoiSubstituida(atual)) {
                await this.recuperarCapturaSubstituida(
                  atual,
                  documentoBaseLocal,
                  documentoLocal,
                  geracao
                );
                return;
              }
              const documentoRemoto = this.normalizarDocumento(
                dados.document || (atual.extraction && atual.extraction.document)
              );
              this.mesclarConflito(documentoBaseLocal, documentoLocal, documentoRemoto);
              if (this.capturaEncerrada) {
                this.erroOperacao = 'A captura está encerrada ou expirada. A revisão atual é somente leitura.';
                this.operacaoParaRepetir = null;
              } else {
                this.erroOperacao = 'A revisão mudou no servidor. Os dados atuais foram carregados; tente a extração novamente.';
                this.operacaoParaRepetir = 'extrair';
              }
            } catch (erroConsulta) {
              if (geracao !== this.geracao) return;
              this.erroOperacao = `A revisão mudou no servidor, mas não foi possível carregar a versão atual: ${mensagemErro(erroConsulta)}`;
              this.operacaoParaRepetir = 'atualizar-conflito';
            }
          } else {
            this.carregarCapturaDoErro(error);
            if (preservarDocumento && this.captura && this.captura.id === captureId) {
              this.restaurarCorrecoes(correcoesLocais, conflitosPreservados);
            }
            this.erroOperacao = mensagemErro(error);
            this.operacaoParaRepetir = 'extrair';
          }
        }
      } finally {
        if (geracao === this.geracao) {
          this.ocupado = false;
          this.operacaoPendente = null;
        }
      }
    },
    restaurarCorrecoes(correcoes, conflitosPreservados = []) {
      this.baseDocumento = JSON.stringify(this.documento);
      this.documento = { ...this.documento, ...correcoes };
      this.conflitosCampos = [...new Set([...this.conflitosCampos, ...conflitosPreservados])];
      this.correcoesMantidas = Object.keys(correcoes).length > 0;
    },
    marcarEditado() {
      this.erroOperacao = null;
    },
    async copiarCorrecoes() {
      const resumo = this.resumoCorrecoesRecuperaveis;
      if (!resumo) return;
      this.mensagemCopiaCorrecoes = null;
      if (navigator.clipboard && navigator.clipboard.writeText) {
        const copiado = await Promise.resolve()
          .then(() => navigator.clipboard.writeText(resumo))
          .then(() => true, () => false);
        if (copiado) {
          this.mensagemCopiaCorrecoes = 'Correções copiadas. Você pode descartar a foto.';
          return;
        }
      }
      const campo = this.$refs.resumoCorrecoes;
      if (campo) {
        campo.focus();
        campo.select();
        if (copiarSelecao()) {
          this.mensagemCopiaCorrecoes = 'Correções copiadas. Você pode descartar a foto.';
          return;
        }
      }
      this.mensagemCopiaCorrecoes = 'Não foi possível copiar automaticamente. O resumo está selecionado; copie-o antes de descartar a foto.';
    },
    async salvarRevisao() {
      if (!this.captura || !this.captura.id || this.capturaEncerrada || !this.alterado || this.salvando || this.ocupado || ['atualizar-conflito', 'recuperar-substituicao'].includes(this.operacaoParaRepetir)) return;
      this.salvando = true;
      this.erroOperacao = null;
      this.operacaoPendente = 'revisar';
      const captureId = this.captura.id;
      const geracao = this.geracao;
      const capturaContinuaAtual = () => (
        geracao === this.geracao && this.captura && this.captura.id === captureId
      );
      const documentoLocal = { ...this.documento };
      const documentoBaseLocal = JSON.parse(this.baseDocumento);
      const correcoes = campos.reduce((doc, chave) => {
        doc[chave] = documentoLocal[chave] === '' ? null : documentoLocal[chave];
        return doc;
      }, {});
      for (let i = 0; i < camposValor.length; i += 1) {
        const campo = camposValor[i].chave;
        const centavos = inputParaCentavos(correcoes[campo]);
        if (centavos === undefined) {
          this.erroOperacao = 'Informe valores válidos em reais, com até duas casas decimais.';
          this.salvando = false;
          this.operacaoPendente = null;
          return;
        }
        correcoes[campo] = centavos;
      }
      const baseRevision = this.revisaoAtual();
      try {
        const resposta = await api.revisar(captureId, correcoes, baseRevision);
        if (!capturaContinuaAtual()) return;
        this.atualizarCaptura(resposta);
        this.conflito = false;
        this.conflitosCampos = [];
        this.correcoesMantidas = false;
        this.operacaoParaRepetir = null;
      } catch (error) {
        if (!capturaContinuaAtual()) return;
        if (Number(error.status) === 409) {
          this.conflito = true;
          this.erroOperacao = null;
          this.operacaoParaRepetir = null;
          try {
            const atual = await api.consultar(captureId);
            if (!capturaContinuaAtual()) return;
            const dados = atual || {};
            const remoto = dados.capture || dados.captura || dados;
            this.adotarCaptura(remoto);
            if (capturaFoiSubstituida(remoto)) {
              await this.recuperarCapturaSubstituida(remoto, documentoBaseLocal, documentoLocal, geracao);
            } else {
              const documentoRemoto = this.normalizarDocumento(
                remoto.extraction && remoto.extraction.document
              );
              this.mesclarConflito(documentoBaseLocal, documentoLocal, documentoRemoto);
            }
          } catch (refreshError) {
            if (!capturaContinuaAtual()) return;
            this.erroOperacao = `Conflito de revisão. Suas correções foram mantidas, mas não foi possível atualizar a versão atual: ${mensagemErro(refreshError)}`;
            this.operacaoParaRepetir = 'atualizar-conflito';
          }
        } else {
          const capturaAtualizada = this.carregarCapturaDoErro(error);
          if (capturaAtualizada) {
            const documentoRemoto = this.normalizarDocumento(
              capturaAtualizada.extraction && capturaAtualizada.extraction.document
            );
            this.mesclarConflito(documentoBaseLocal, documentoLocal, documentoRemoto);
            this.conflito = true;
          }
          this.erroOperacao = mensagemErro(error);
          this.operacaoParaRepetir = 'revisar';
        }
      } finally {
        if (geracao === this.geracao) {
          this.salvando = false;
          this.operacaoPendente = null;
        }
      }
    },
    async recarregarConflito() {
      if (!this.captura || !this.captura.id || this.ocupado || this.salvando) return;
      const captureId = this.captura.id;
      const geracao = this.geracao;
      const documentoLocal = { ...this.documento };
      const documentoBaseLocal = JSON.parse(this.baseDocumento);
      this.ocupado = true;
      this.erroOperacao = null;
      try {
        const resposta = await api.consultar(captureId);
        if (geracao !== this.geracao || !this.captura || this.captura.id !== captureId) return;
        const dados = resposta || {};
        const atual = dados.capture || dados.captura || dados;
        this.adotarCaptura(atual);
        if (capturaFoiSubstituida(atual)) {
          await this.recuperarCapturaSubstituida(atual, documentoBaseLocal, documentoLocal, geracao);
        } else {
          const documentoRemoto = this.normalizarDocumento(
            atual.extraction && atual.extraction.document
          );
          this.mesclarConflito(documentoBaseLocal, documentoLocal, documentoRemoto);
        }
        this.conflito = true;
        if (!capturaFoiSubstituida(atual) && !this.replacementIdParaRecuperar) this.operacaoParaRepetir = null;
      } catch (error) {
        if (geracao === this.geracao) {
          this.erroOperacao = `Conflito de revisão. Suas correções foram mantidas, mas não foi possível atualizar a versão atual: ${mensagemErro(error)}`;
          this.operacaoParaRepetir = 'atualizar-conflito';
        }
      } finally {
        if (geracao === this.geracao) this.ocupado = false;
      }
    },
    async descartar() {
      if (!this.captura || !this.captura.id || this.ocupado || capturaFoiSubstituida(this.captura) || this.operacaoParaRepetir === 'recuperar-substituicao') return;
      const id = this.captura.id;
      const geracao = ++this.geracao;
      this.ocupado = true;
      this.estadoExtracao = 'enviando';
      try {
        await api.descartar(id);
        if (geracao === this.geracao) this.limpar();
      } catch (error) {
        if (geracao === this.geracao) {
          this.erroOperacao = mensagemErro(error);
          this.operacaoParaRepetir = 'descartar';
        }
      } finally {
        if (geracao === this.geracao) this.ocupado = false;
      }
    },
    repetirOperacao() {
      if (this.ocupado) return;
      if (this.capturaEncerrada && !['descartar', 'recuperar-substituicao'].includes(this.operacaoParaRepetir)) return;
      if (this.operacaoParaRepetir === 'extrair') return this.extrair();
      if (this.operacaoParaRepetir === 'revisar') return this.salvarRevisao();
      if (this.operacaoParaRepetir === 'atualizar-conflito') return this.recarregarConflito();
      if (this.operacaoParaRepetir === 'reconciliar-captura-inicial') return this.reconciliarCapturaInicial();
      if (this.operacaoParaRepetir === 'recuperar-substituicao') {
        if (this.replacementIdParaRecuperar) return this.repetirBuscaSubstituicao();
        return this.recuperarSubstituicao(this.capturaParaRecuperar);
      }
      if (this.operacaoParaRepetir === 'substituir' || this.operacaoParaRepetir === 'enviar') {
        if (this.arquivoPendente) return this.enviarArquivo(this.arquivoPendente);
      }
      if (this.operacaoParaRepetir === 'descartar') return this.descartar();
      this.erroOperacao = null;
    },
    async repetirBuscaSubstituicao() {
      const replacementId = this.replacementIdParaRecuperar;
      if (!replacementId || this.ocupado) return;
      const geracao = this.geracao;
      const documentoLocal = { ...this.documento };
      const documentoBaseLocal = JSON.parse(this.baseDocumento);
      this.ocupado = true;
      this.erroOperacao = null;
      try {
        const resposta = await api.consultar(replacementId);
        if (geracao !== this.geracao) return;
        await this.seguirCadeiaSubstituicoes(
          resposta,
          documentoBaseLocal,
          documentoLocal,
          geracao,
          [this.captura && this.captura.id]
        );
      } catch (error) {
        if (geracao === this.geracao) {
          this.replacementIdParaRecuperar = replacementId;
          this.erroOperacao = `A captura antiga foi substituída, mas não foi possível carregar a nova. Suas correções foram mantidas; tente consultar novamente: ${mensagemErro(error)}`;
          this.operacaoParaRepetir = 'recuperar-substituicao';
        }
      } finally {
        if (geracao === this.geracao) this.ocupado = false;
      }
    },
    limpar() {
      this.geracao += 1;
      this.captura = null;
      this.revogarPreview(this.previewPrivado);
      this.previewPrivado = null;
      this.documento = documentoVazio();
      this.baseDocumento = JSON.stringify(this.documento);
      this.erroCaptura = null;
      this.erroOperacao = null;
      this.erroPreview = null;
      this.conflito = false;
      this.capturaInicialIncerta = false;
      this.chaveIdempotenciaCaptura = null;
      this.contextoCapturaInicial = null;
      this.capturaEncerrada = false;
      this.conflitosCampos = [];
      this.correcoesMantidas = false;
      this.mensagemCopiaCorrecoes = null;
      this.estadoExtracao = 'ociosa';
      this.ocupado = false;
      this.salvando = false;
      this.operacaoPendente = null;
      this.arquivoPendente = null;
      this.capturaParaRecuperar = null;
      this.replacementIdParaRecuperar = null;
      this.operacaoParaRepetir = null;
    },
    mesclarConflito(documentoBaseAnterior, documentoLocal, documentoRemoto) {
      const correcoes = campos.reduce((resultado, chave) => {
        if (documentoLocal[chave] !== documentoBaseAnterior[chave]) {
          resultado[chave] = documentoLocal[chave];
        }
        return resultado;
      }, {});
      this.conflitosCampos = Object.keys(correcoes).filter(chave => (
        documentoRemoto[chave] !== documentoBaseAnterior[chave]
        && documentoRemoto[chave] !== correcoes[chave]
      ));
      this.baseDocumento = JSON.stringify(documentoRemoto);
      this.documento = { ...documentoRemoto, ...correcoes };
      this.mensagemCopiaCorrecoes = null;
    },
    async recuperarCapturaSubstituida(capturaAntiga, documentoBaseLocal, documentoLocal, geracao) {
      this.mesclarConflito(
        documentoBaseLocal,
        documentoLocal,
        this.normalizarDocumento(capturaAntiga.extraction && capturaAntiga.extraction.document)
      );
      await this.seguirCadeiaSubstituicoes(
        capturaAntiga,
        documentoBaseLocal,
        documentoLocal,
        geracao,
        []
      );
    },
    async seguirCadeiaSubstituicoes(respostaInicial, documentoBaseLocal, documentoLocal, geracao, idsVisitados = []) {
      const visitados = new Set(idsVisitados.filter(idCapturaValido).map(String));
      let resposta = respostaInicial;
      let atual = null;
      let passos = 0;

      while (geracao === this.geracao && passos <= MAX_SUBSTITUICOES_ENCADEADAS) {
        const dados = resposta || {};
        atual = dados.capture || dados.captura || dados;
        const idAtual = atual && atual.id;

        if (!capturaFoiSubstituida(atual)) {
          this.atualizarCaptura(resposta);
          this.capturaEncerrada = estadoCapturaEncerrado(this.captura);
          this.mesclarConflito(
            documentoBaseLocal,
            documentoLocal,
            this.normalizarDocumento(this.captura && this.captura.extraction && this.captura.extraction.document)
          );
          this.replacementIdParaRecuperar = null;
          this.capturaParaRecuperar = null;
          this.conflito = true;
          if (this.capturaEncerrada) {
            this.operacaoParaRepetir = null;
            this.erroOperacao = 'A captura atual está encerrada ou expirada. A revisão está somente para leitura.';
            return false;
          }
          this.operacaoParaRepetir = null;
          await this.carregarPreview(this.captura.id, geracao);
          await this.extrair(geracao);
          this.conflito = true;
          return true;
        }

        if (idCapturaValido(idAtual)) visitados.add(String(idAtual));
        const replacementId = atual && atual.replacement_id;
        const idRetentativa = idCapturaValido(replacementId) ? replacementId : idAtual;
        const ciclo = idCapturaValido(replacementId) && visitados.has(String(replacementId));
        if (!idCapturaValido(replacementId) || ciclo || passos === MAX_SUBSTITUICOES_ENCADEADAS) {
          this.adotarCaptura(atual);
          this.capturaEncerrada = true;
          this.mesclarConflito(
            documentoBaseLocal,
            documentoLocal,
            this.normalizarDocumento(atual.extraction && atual.extraction.document)
          );
          this.capturaParaRecuperar = idCapturaValido(idAtual) ? idAtual : this.capturaParaRecuperar;
          this.replacementIdParaRecuperar = idCapturaValido(idRetentativa) ? idRetentativa : null;
          this.conflito = true;
          this.erroOperacao = ciclo
            ? 'A cadeia de substituições contém um ciclo. Suas correções foram mantidas; tente consultar novamente.'
            : (!idCapturaValido(replacementId)
              ? 'A captura substituída não informou um replacement_id válido. Suas correções foram mantidas.'
              : 'A cadeia de substituições excedeu o limite de segurança. Suas correções foram mantidas; tente consultar novamente.');
          this.operacaoParaRepetir = 'recuperar-substituicao';
          return false;
        }

        try {
          resposta = await api.consultar(replacementId);
        } catch (error) {
          if (geracao !== this.geracao) return false;
          this.adotarCaptura(atual);
          this.capturaEncerrada = true;
          this.mesclarConflito(
            documentoBaseLocal,
            documentoLocal,
            this.normalizarDocumento(atual.extraction && atual.extraction.document)
          );
          this.capturaParaRecuperar = idCapturaValido(idAtual) ? idAtual : this.capturaParaRecuperar;
          this.replacementIdParaRecuperar = replacementId;
          this.conflito = true;
          this.erroOperacao = `A captura antiga foi substituída, mas não foi possível carregar a próxima. Suas correções foram mantidas; tente consultar novamente: ${mensagemErro(error)}`;
          this.operacaoParaRepetir = 'recuperar-substituicao';
          return false;
        }
        passos += 1;
      }
      return false;
    },
  },
};
</script>

<style scoped>
.drop-area {
  border: 1px dashed #9e9e9e;
  border-radius: 4px;
}
.drop-area--ativa {
  border-color: #1976d2;
  background: #e3f2fd;
}
.preview-foto {
  min-height: 180px;
  background: #f5f5f5;
  border-radius: 4px;
}
.preview-foto img {
  display: block;
  max-width: 100%;
  max-height: 320px;
  object-fit: contain;
}
.resumo-correcoes {
  display: block;
  width: 100%;
  resize: vertical;
  font-family: monospace;
}
.campo-sorteio {
  background: #fff8e1;
  border-radius: 4px;
}
.aviso-sorteio {
  background: #fff8e1;
  border-left: 4px solid #f9a825;
  border-radius: 4px;
}
</style>
